import React, { useEffect, useRef } from 'react';
import { useCookieConsent } from '../../lib/hooks/useCookieConsent';
import './CookieSettingsModal.css';

interface CookieSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

interface CategoryRowProps {
  label: string;
  description: string;
  cookieCount: string;
  enabled: boolean;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
}

function CategoryRow({ label, description, cookieCount, enabled, disabled, onChange }: CategoryRowProps) {
  return (
    <div className="cookie-modal__category">
      <div className="cookie-modal__category-info">
        <div className="cookie-modal__category-label">{label}</div>
        <div className="cookie-modal__category-desc">{description}</div>
        <div className="cookie-modal__category-count">{cookieCount}</div>
      </div>
      <div className="cookie-modal__toggle-wrap">
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={`${label} cookies`}
          disabled={disabled}
          onClick={() => onChange?.(!enabled)}
          className={`cookie-modal__toggle${enabled ? ' cookie-modal__toggle--on' : ''}${disabled ? ' cookie-modal__toggle--disabled' : ''}`}
        >
          <span className="cookie-modal__toggle-knob" />
        </button>
      </div>
    </div>
  );
}

export default function CookieSettingsModal({ open, onClose }: CookieSettingsModalProps) {
  const { consent, update } = useCookieConsent();
  const closeRef = useRef<HTMLButtonElement>(null);

  // Focus the close button when modal opens
  useEffect(() => {
    if (open) {
      closeRef.current?.focus();
    }
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const functional = consent?.functional ?? false;
  const analytics = consent?.analytics ?? false;

  const handleSave = () => {
    update({ shown: true });
    onClose();
  };

  return (
    <div
      className="cookie-modal__backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      aria-modal="true"
      role="dialog"
      aria-labelledby="cookie-modal-title"
    >
      <div className="cookie-modal">
        <div className="cookie-modal__header">
          <h2 id="cookie-modal-title" className="cookie-modal__title">Cookie settings</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="cookie-modal__close"
            aria-label="Close cookie settings"
          >
            ✕
          </button>
        </div>

        <div className="cookie-modal__body">
          <CategoryRow
            label="Strictly necessary"
            description="Required for the site to work. Cannot be turned off."
            cookieCount="1 cookie: msr_cookie_consent"
            enabled={true}
            disabled={true}
          />
          <CategoryRow
            label="Functional"
            description="Remember your preferences across visits."
            cookieCount="0 cookies today"
            enabled={functional}
            onChange={(v) => update({ functional: v })}
          />
          <CategoryRow
            label="Analytics"
            description="Help us understand how people use the site."
            cookieCount="0 cookies today"
            enabled={analytics}
            onChange={(v) => update({ analytics: v })}
          />
          <CategoryRow
            label="Marketing"
            description="We don't use these."
            cookieCount="We don't use marketing cookies."
            enabled={false}
            disabled={true}
          />
        </div>

        <div className="cookie-modal__footer">
          <button type="button" onClick={handleSave} className="cookie-modal__save">
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}
